import { SubscriptionAddon, SubscriptionModule } from "@/constants/workplaces";

export enum RolesAction {
  ADD = "addRole",
  EDIT = "editRole",
  DUPLICATE = "duplicateRole",
  DELETE = "deleteRole",
  UNABLE_TO_DELETE = "unableToDeleteRole",
}

export enum MspRolesAction {
  ADD = "addMspRole",
  EDIT = "editMspRole",
  DUPLICATE = "duplicateMspRole",
  DELETE = "deleteMspRole",
  UNABLE_TO_DELETE = "unableToDeleteMspRole",
}

export enum GlobalRolesAction {
  ADD = "addGlobalRole",
  EDIT = "editGlobalRole",
  DUPLICATE = "duplicateGlobalRole",
  DELETE = "deleteGlobalRole",
  UNABLE_TO_DELETE = "unableToDeleteGlobalRole",
}

export enum RoleType {
  WORKSPACE = "WORKSPACE",
  MSP = "MSP",
  GLOBAL = "GLOBAL",
}

export enum RolePermissionAccessMode {
  VIEW = "view",
  EDIT = "edit",
  NO_ACCESS = "noAccess",
}

/**
 * Top-level permission scopes shared by every role type (workspace, MSP and global).
 * Values are the literal keys of the `WorkspaceRolePermissions` object, e.g.
 * `permissions[RolePermissionsScope.PROTECTION]`.
 */
export enum RolePermissionsScope {
  WORKSPACE_MANAGEMENT = "workspaceManagementScope",
  VIEWS = "viewsScope",
  PROTECTION = "protectionScope",
  TICKETS = "ticketsScope",
}

/**
 * Extra top-level scope that exists only on global roles. Its single value is the key
 * of the `GlobalScope` block inside `GlobalRolePermissions`.
 */
export enum GlobalRoleScopes {
  GLOBAL_SCOPE = "globalScope",
}

/**
 * Sections inside the global scope (`globalScope`). Values are the literal keys of the
 * `GlobalScope` object — global admin and role management, SOC portal access, plus the
 * workspace-visibility and special-permission toggles.
 */
export enum GlobalRoleScopeSection {
  GLOBAL_ADMIN_USERS = "globalAdminsAccess",
  GLOBAL_ROLES = "globalRolesAccess",
  SOC_PORTAL = "socPortalAccess",
  WORKSPACE_SUBSCRIPTION_TYPE_ACCESS = "workspaceSubscriptionTypeAccess",
  WORKSPACE_TYPE_ACCESS = "workspaceTypeAccess",
  SPECIAL_PERMISSIONS = "specialPermissions",
}

/**
 * Extra top-level scope added for MSP (and global) roles. Its single value is the key
 * of the `MspPortalScope` block inside `MspPortalPermissions`.
 */
export enum MspRolePermissionScopes {
  MSP_PORTAL_SCOPE = "mspPortalScope",
}

/**
 * Sections inside the MSP portal scope (`mspPortalScope`). Values are the literal keys of
 * the `MspPortalScope` object — workspace, channel-admin and channel-role management,
 * labels, reports, global policies, allow/block lists and MSP special permissions.
 */
export enum MspPortalScopeSection {
  MSP_WORKSPACES = "mspPortalAccess",
  MSP_ADMIN_USERS = "channelAdminsAccess",
  MSP_ROLES = "channelRolesAccess",
  MSP_WORKSPACE_LABELS = "workspaceLabelsAccess",
  MSP_REPORTS = "mspReportsAccess",
  MSP_GLOBAL_POLICIES = "mspPoliciesAccess",
  MSP_SPECIAL_PERMISSIONS = "mspSpecialPermissions",
  MSP_ALLOW_BLOCK_LISTS = "mspAllowBlockListAccess",
  MSP_BACKUP_AND_RESTORE = "mspBackupAndRestoreAccess",
}

/**
 * Non-subscription entries inside the protection scope. Subscription-backed modules are
 * keyed by `SubscriptionModule` / `SubscriptionAddon`; this enum covers the extra platform
 * capabilities (currently only Managed SOC) that share the same `protectionScope`.
 */
export enum ProtectionScopeModules {
  MANAGED_SOC = "managedSoc",
}

/**
 * Sections inside the workspace-management scope (`workspaceManagementScope`). Values are
 * the literal keys of the `WorkspaceManagementScope` object (users, devices, connectors,
 * roles, admin users, …).
 */
export enum WorkspaceManagementScopeSections {
  USERS = "users",
  DEVICES = "devices",
  CLOUD_APPS = "cloudApps",
  ACTIVE_SESSIONS = "activeSessions",
  ACTIVITY_LOGS = "activityLogs",
  CONNECTORS = "connectors",
  ROLES = "roles",
  REPORTS = "reports",
  ADMIN_USERS = "adminUsers",
}

export const emptyPermissions = {
  name: null,
  [RolePermissionsScope.WORKSPACE_MANAGEMENT]: {
    [WorkspaceManagementScopeSections.USERS]: null,
    [WorkspaceManagementScopeSections.DEVICES]: null,
    [WorkspaceManagementScopeSections.CLOUD_APPS]: null,
    [WorkspaceManagementScopeSections.ACTIVE_SESSIONS]: null,
    [WorkspaceManagementScopeSections.ACTIVITY_LOGS]: null,
    [WorkspaceManagementScopeSections.CONNECTORS]: null,
    [WorkspaceManagementScopeSections.ROLES]: null,
    [WorkspaceManagementScopeSections.REPORTS]: null,
    [WorkspaceManagementScopeSections.ADMIN_USERS]: {
      accessMode: null,
      editAccessModePermission: {
        add: true,
        edit: true,
        remove: true,
        assignRoles: false,
        manageContentPermissions: true,
        removeMfaData: true,
      },
    },
  },
  [RolePermissionsScope.VIEWS]: {
    usersView: null,
    devicesView: null,
  },
  [RolePermissionsScope.PROTECTION]: {
    [SubscriptionModule.CLOUD_SECURITY]: null,
    [SubscriptionModule.ENDPOINT_SECURITY]: null,
    [SubscriptionModule.EMAIL_SECURITY]: null,
    [SubscriptionModule.USER_DATA_GOVERNANCE]: null,
    [SubscriptionModule.ENDPOINT_DATA_GOVERNANCE]: null,
    [SubscriptionModule.EDR]: null,
    [SubscriptionModule.NETWORK]: null,
    [ProtectionScopeModules.MANAGED_SOC]: null,
    [SubscriptionAddon.INBOUND_GATEWAY]: null,
    [SubscriptionModule.MDM]: null,
    [SubscriptionModule.SECURITY_AWARENESS_TRAINING]: null,
    [SubscriptionModule.SWG]: null,
    [SubscriptionModule.BACKUP_AND_RESTORE]: null,
    [SubscriptionAddon.WIFI_PHISHING]: null,
    [SubscriptionAddon.SECURED_MESSAGES]: null,
  },
  [RolePermissionsScope.TICKETS]: {
    [SubscriptionModule.EDR]: {
      accessMode: null,
      editAccessModePermission: {
        lowSeverity: true,
        mediumSeverity: true,
        criticalSeverity: true,
        commentTickets: true,
      },
    },
    [SubscriptionModule.ENDPOINT_SECURITY]: {
      accessMode: null,
      editAccessModePermission: {
        lowSeverity: true,
        mediumSeverity: true,
        criticalSeverity: true,
        commentTickets: true,
      },
    },
    [SubscriptionModule.ENDPOINT_DATA_GOVERNANCE]: {
      accessMode: null,
      editAccessModePermission: {
        lowSeverity: true,
        mediumSeverity: true,
        criticalSeverity: true,
        commentTickets: true,
      },
    },
    [SubscriptionModule.EMAIL_SECURITY]: {
      accessMode: null,
      editAccessModePermission: {
        lowSeverity: true,
        mediumSeverity: true,
        criticalSeverity: true,
        commentTickets: true,
      },
    },
    [SubscriptionModule.CLOUD_SECURITY]: {
      accessMode: null,
      editAccessModePermission: {
        lowSeverity: true,
        mediumSeverity: true,
        criticalSeverity: true,
        commentTickets: true,
      },
    },
    [SubscriptionModule.USER_DATA_GOVERNANCE]: {
      accessMode: null,
      editAccessModePermission: {
        lowSeverity: true,
        mediumSeverity: true,
        criticalSeverity: true,
        commentTickets: true,
      },
    },
  },
};

export const emptyMspPermissions = {
  [MspRolePermissionScopes.MSP_PORTAL_SCOPE]: {
    [MspPortalScopeSection.MSP_WORKSPACES]: {
      accessMode: null,
      editAccessModePermission: {
        editWorkspace: true,
        startSubscription: true,
        stopSubscription: true,
        editSubscription: true,
        createChildWorkspace: true,
        createChannelWorkspace: true,
        archiveWorkspace: true,
      },
    },
    [MspPortalScopeSection.MSP_ADMIN_USERS]: {
      accessMode: null,
      editAccessModePermission: {
        add: true,
        remove: true,
        assignRoles: true,
      },
    },
    [MspPortalScopeSection.MSP_ROLES]: {
      accessMode: null,
      editAccessModePermission: {
        add: true,
        edit: true,
        remove: true,
      },
    },
    [MspPortalScopeSection.MSP_WORKSPACE_LABELS]: null,
    [MspPortalScopeSection.MSP_GLOBAL_POLICIES]: null,
    [MspPortalScopeSection.MSP_REPORTS]: null,
    [MspPortalScopeSection.MSP_ALLOW_BLOCK_LISTS]: null,
    [MspPortalScopeSection.MSP_BACKUP_AND_RESTORE]: null,
    [MspPortalScopeSection.MSP_SPECIAL_PERMISSIONS]: {
      demoMode: true,
    },
  },
  ...emptyPermissions,
};

export const emptyGlobalPermissions = {
  [GlobalRoleScopes.GLOBAL_SCOPE]: {
    [GlobalRoleScopeSection.GLOBAL_ADMIN_USERS]: {
      accessMode: null,
      editAccessModePermission: {
        add: true,
        remove: true,
        assignRoles: true,
      },
    },
    [GlobalRoleScopeSection.GLOBAL_ROLES]: {
      accessMode: null,
      editAccessModePermission: {
        add: true,
        remove: true,
        edit: true,
      },
    },
    [GlobalRoleScopeSection.SOC_PORTAL]: {
      accessMode: null,
      editAccessModePermission: {
        lowSeverity: true,
        mediumSeverity: true,
        criticalSeverity: true,
        commentTickets: true,
        tier1Statuses: true,
        tier2Statuses: true,
      },
    },
    [GlobalRoleScopeSection.WORKSPACE_SUBSCRIPTION_TYPE_ACCESS]: {
      prospects: true,
      customers: true,
    },
    [GlobalRoleScopeSection.WORKSPACE_TYPE_ACCESS]: {
      regular: true,
      channel: true,
      child: true,
    },
    [GlobalRoleScopeSection.SPECIAL_PERMISSIONS]: {
      collectLogs: true,
      collectQuarantineData: true,
      showAdvancedDeviceInformation: true,
      backOffice: true,
      manageEndpointAutoUpdateGroups: true,
      reportPositiveEmail: true,
      sendCoursePreview: true,
      // TODO: uncomment in future when needed again
      // manageWorkspaceCodes: true,
    },
  },
  [MspRolePermissionScopes.MSP_PORTAL_SCOPE]: {
    [MspPortalScopeSection.MSP_WORKSPACES]: {
      accessMode: null,
      editAccessModePermission: {
        editWorkspace: true,
        startSubscription: true,
        stopSubscription: true,
        editSubscription: true,
        createChildWorkspace: true,
        createChannelWorkspace: true,
        createRegularWorkspace: true,
        archiveWorkspace: true,
        extendTrial: true,
        convertWorkspace: true,
        restoreWorkspace: true,
        startTrial: true,
      },
    },
    [MspPortalScopeSection.MSP_ADMIN_USERS]: {
      accessMode: null,
      editAccessModePermission: {
        add: true,
        remove: true,
        assignRoles: true,
      },
    },
    [MspPortalScopeSection.MSP_ROLES]: {
      accessMode: null,
      editAccessModePermission: {
        add: true,
        edit: true,
        remove: true,
      },
    },
    [MspPortalScopeSection.MSP_WORKSPACE_LABELS]: null,
    [MspPortalScopeSection.MSP_GLOBAL_POLICIES]: null,
    [MspPortalScopeSection.MSP_REPORTS]: null,
    [MspPortalScopeSection.MSP_ALLOW_BLOCK_LISTS]: null,
    [MspPortalScopeSection.MSP_BACKUP_AND_RESTORE]: null,
    [MspPortalScopeSection.MSP_SPECIAL_PERMISSIONS]: {
      demoMode: true,
    },
  },
  ...emptyPermissions,
};
